/**
 * Sawyer C3 — the admin client for a groom's customer-requested add-ons.
 *
 *   GET  /api/bookings/admin/:id/requested-add-ons        → getRequestedAddOns
 *   POST /api/bookings/admin/:id/requested-add-ons/apply  → applyRequestedAddOns
 *
 * The read never throws: it answers a discriminated result so the tier
 * dialog can draw every state honestly —
 *   ok          the requests (possibly none: the section then renders nothing);
 *   unsupported an OLDER server during the deploy window: the admin guard
 *               answers 403 `{ error: 'forbidden', reason: 'route_not_mapped' }`
 *               for a route it does not know, so the section is hidden and the
 *               checkout behaves exactly as before (no apply is ever sent);
 *   error       network / 5xx / a body that does not parse — the section says
 *               it could not load, and confirming bills no add-on the desk
 *               could not see.
 * Prices are the server's (`priceServiceLeg`); nothing here computes one.
 */
import { z } from 'zod';
import { ApiError, parseErrorBody } from './api.js';

const requestedAddOnSchema = z.object({
  addOnId: z.string(),
  label: z.string(),
  requestedPriceCents: z.number().int(),
  catalogPriceCents: z.number().int(),
  priceChanged: z.boolean(),
  totalCents: z.number().int().nullable(),
  status: z.enum(['billable', 'already_added', 'unavailable']),
  legId: z.string().nullable(),
});

const requestedAddOnsViewSchema = z.object({
  bookingId: z.string(),
  dogCount: z.number().int().nonnegative(),
  requested: z.array(requestedAddOnSchema),
});

export type RequestedAddOn = z.infer<typeof requestedAddOnSchema>;

export type RequestedAddOnsResult =
  | { readonly kind: 'ok'; readonly dogCount: number; readonly requested: readonly RequestedAddOn[] }
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'error'; readonly message: string };

export const REQUESTED_ADD_ONS_LOAD_ERROR = "Couldn't load the customer's add-on requests.";

function isRouteNotMapped(status: number, body: unknown): boolean {
  return (
    status === 403 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { reason?: unknown }).reason === 'route_not_mapped'
  );
}

export async function getRequestedAddOns(
  apiUrl: string,
  token: string,
  bookingId: string,
): Promise<RequestedAddOnsResult> {
  let res: Response;
  try {
    res = await fetch(`${apiUrl}/api/bookings/admin/${bookingId}/requested-add-ons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return { kind: 'error', message: REQUESTED_ADD_ONS_LOAD_ERROR };
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  if (isRouteNotMapped(res.status, body)) return { kind: 'unsupported' };
  if (!res.ok) return { kind: 'error', message: REQUESTED_ADD_ONS_LOAD_ERROR };
  const parsed = requestedAddOnsViewSchema.safeParse(body);
  if (!parsed.success) return { kind: 'error', message: REQUESTED_ADD_ONS_LOAD_ERROR };
  return { kind: 'ok', dogCount: parsed.data.dogCount, requested: parsed.data.requested };
}

export interface ApplyRequestedAddOnsOutcome {
  readonly createdIds: readonly string[];
  readonly skipped: ReadonlyArray<{ addOnId: string; legId: string; reason: 'already_added' }>;
}

const applyResponseSchema = z.object({
  created: z.array(z.object({ id: z.string() }).passthrough()),
  skipped: z.array(z.object({ addOnId: z.string(), legId: z.string(), reason: z.literal('already_added') })),
});

/** Throws an `ApiError` carrying the server's human message on any non-2xx. */
export async function applyRequestedAddOns(
  apiUrl: string,
  token: string,
  bookingId: string,
  addOnIds: readonly string[],
): Promise<ApplyRequestedAddOnsOutcome> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${bookingId}/requested-add-ons/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ addOnIds }),
  });
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  if (!res.ok) {
    const parsed = parseErrorBody(res.status, body, { fallback: `Could not add the requested add-ons (${res.status})` });
    throw new ApiError(res.status, parsed.message, parsed.code);
  }
  const parsed = applyResponseSchema.safeParse(body);
  if (!parsed.success) return { createdIds: [], skipped: [] };
  return { createdIds: parsed.data.created.map((c) => c.id), skipped: parsed.data.skipped };
}
